import { Grid } from '@mui/material';
import { isEmpty } from 'lodash';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FilterList } from 'src/components/filterList';
import { Header } from 'src/components/header';
import { ActionBar } from 'src/components/header/actionBar';
import { SortOptions } from 'src/components/subHeader/SubHeaderWithSorting.interface';
import { PATHS } from 'src/constants/paths';
import { useFilterAndSorting, useParamQuery } from 'src/hooks';
import { useSearchFilter } from 'src/hooks/useSearchFilter';
import type { IFilter } from 'src/interfaces';
import { lang, normalizeSearchKeywordsBackslashes } from 'src/utils';

import { ManageCategoryLink } from '../addProductCategoryPage/AddProductCategoryPage.styled';
import { useListProductsPage } from './hooks/ListProductsPage.hook';
import { formatProductGridData } from './modules/productGrid/ProductGrid.helper';
import { ProductList } from './modules/productList/ProductList';

export const ListProductsPage = () => {
  const {
    productVariationTypes,
    productCategories,
    onChangeParams,
    infiniteProductsData,
    isLoadingInfiniteProducts,
    getNextProductPage,
    errorLoadingInfiniteProducts,
    onSortChangeHandle,
    selectedSortOption,
    templates,
    isTemplateLoading,
    scrollRef,
  } = useListProductsPage();

  const { getParamsValue, activeFilters } = useParamQuery();
  const { handleOnChangeFilterKeyword, handleOnRemoveFilterKeyword } =
    useSearchFilter();
  const navigate = useNavigate();
  const filters: IFilter[] = [
    {
      label: lang.productCode,
      options: [],
      isLoading: false,
      fieldType: 'filterWithSearch',
      badgeOptions: {
        align: 'top',
        color: 'primary',
      },
    },
    {
      label: 'product_variation_flavor_id',
      labelOverride: lang.flavor,
      options: [],
      isLoading: false,
      fieldType: 'filterWithSearch',
      badgeOptions: {
        align: 'top',
        color: 'primary',
      },
    },
    {
      labelOverride: 'Product variation name',
      label: 'product_name',
      isLoading: false,
      options: [],
      fieldType: 'freeSearch',
      filterSearchVal: getParamsValue('productVariations.name.regexp'),
      onChangeFilterKeyword: (value: string) =>
        handleOnChangeFilterKeyword(value, 'productVariations.name.regexp'),
      onRemoveFilterKeyword: (value: string) =>
        handleOnRemoveFilterKeyword(value, 'productVariations.name.regexp'),
    },
    {
      label: 'Product category',
      options: productCategories.map((item) => ({
        label: item.name,
        value: item.id,
      })),
      isLoading: false,
      customCta: (
        <ManageCategoryLink
          component="button"
          variant="body2"
          onClick={() => navigate(PATHS.PRODUCT_CATEGORIES_MANAGE)}>
          {lang.manageCategory}
        </ManageCategoryLink>
      ),
    },
    {
      label: 'active',
      labelOverride: 'Status',
      options: [
        { label: 'Active', value: 1 },
        { label: 'Inactive', value: 0 },
      ],
      isLoading: false,
    },
    {
      label: 'product_variation_size_id',
      labelOverride: 'Variation types',
      options: productVariationTypes.map((item) => ({
        label: item.name || 'None',
        value: item.id,
      })),
      isLoading: false,
    },
    {
      label: 'Components',
      options: [
        { label: 'Assigned to component', value: 1 },
        { label: 'Unassigned to component', value: 0 },
      ],
      isLoading: false,
    },
    {
      label: lang.dytemplates,
      options: templates.map((template) => ({
        label: template.template_name.replace('_', ' '),
        value: template.id,
      })),
      isLoading: isTemplateLoading,
    },
  ];

  const [isFuzzySearch, setIsFuzzySearch] = useState(
    !('display_name.list' in activeFilters),
  );
  const {
    onRemoveProductKeyword,
    onReset,
    onChangeToExactMatch,
    onChangeToFuzzyMatch,
  } = useFilterAndSorting(filters, [], isFuzzySearch, setIsFuzzySearch);

  const onChangeKeyword = (value: string | string[]) => {
    const inputValue = Array.isArray(value) ? [...value] : [value];
    const keywords = Array.isArray(value) ? [...value] : [value];
    const current_keyword = keywords.pop() || '';
    if (!current_keyword && keywords.length === 0) {
      onChangeParams(
        `${isFuzzySearch ? 'display_name.filter' : 'display_name.list'}`,
        [],
      );

      return;
    }

    if (!current_keyword) {
      onChangeParams(
        `${isFuzzySearch ? 'display_name.filter' : 'display_name.list'}`,
        keywords,
      );

      return;
    }

    const normalized_keyword =
      normalizeSearchKeywordsBackslashes(current_keyword);
    onChangeParams(
      `${isFuzzySearch ? 'display_name.filter' : 'display_name.list'}`,
      isFuzzySearch ? [...keywords, normalized_keyword as string] : inputValue,
    );

    if (!isEmpty(value))
      onChangeParams('sort', `-${[SortOptions.LAST_MODIFIED]}`);
  };

  const onSearchTypeChange = () => {
    if (isFuzzySearch) {
      onChangeToExactMatch('display_name.filter', 'display_name.list');
      setIsFuzzySearch(false);
    } else {
      onChangeToFuzzyMatch('display_name.list', 'display_name.filter');
      setIsFuzzySearch(true);
    }
  };

  return (
    <Grid>
      <Header
        actionBar={
          <ActionBar
            title={lang.products}
            numResults={formatProductGridData(infiniteProductsData).length}
            maxResults={infiniteProductsData?.pages[0].totalCount}
            type={lang.products}
          />
        }
        title={lang.products}
      />
      <Grid container spacing={4}>
        <Grid item xs={12} md={3}>
          <FilterList
            enableSearchArea
            filters={filters}
            onChangeKeyword={onChangeKeyword}
            onRemoveKeyword={onRemoveProductKeyword}
            onChangeFilter={onChangeParams}
            onReset={onReset}
            showReset
            searchVal={getParamsValue(
              `${isFuzzySearch ? 'display_name.filter' : 'display_name.list'}`,
            )}
            hasExactSearch
            onSearchTypeChange={onSearchTypeChange}
            isFuzzySearch={isFuzzySearch}
          />
        </Grid>

        <Grid item xs={12} md={9}>
          <ProductList
            infiniteProductsData={infiniteProductsData}
            isLoadingInfiniteProducts={isLoadingInfiniteProducts}
            errorLoadingInfiniteProducts={errorLoadingInfiniteProducts}
            onSortChangeHandle={onSortChangeHandle}
            selectedSortOption={selectedSortOption}
            getNextProductPage={getNextProductPage}
            numResults={formatProductGridData(infiniteProductsData).length}
            totalCount={infiniteProductsData?.pages[0].totalCount}
            scrollRef={scrollRef}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
